import { useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Icon } from '@/new/components/icon';
import { type IssueFormItem } from '@/new/components/matter/issues-list';
import { SettingsListHeader, SettingsListItem } from '@/new/components/settings-section';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Textarea } from '@/new/components/shadcn/textarea';

interface ContextIssuesListProps {
  issues: IssueFormItem[];
  onChange: (issues: IssueFormItem[]) => void;
}

/**
 * Issues list styled for the matter settings cards (Linear-style header row +
 * divided item rows). Kept separate from the shared `IssuesList` used in the
 * matter creation flow.
 */
export function ContextIssuesList({ issues, onChange }: ContextIssuesListProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [addLabel, setAddLabel] = useState('');
  const [addText, setAddText] = useState('');

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editText, setEditText] = useState('');

  const handleAdd = () => {
    const trimmedLabel = addLabel.trim();
    if (!trimmedLabel) return;
    const trimmedText = addText.trim();
    onChange([...issues, { id: uuidv4(), label: trimmedLabel, text: trimmedText }]);
    trackCustomEvent(TrackingEventsV2.MatterContextIssueAdd);
    setAddLabel('');
    setAddText('');
    setShowAddModal(false);
  };

  const openEditModal = (index: number) => {
    const issue = issues[index];
    setEditingIndex(index);
    setEditLabel(issue.label);
    setEditText(issue.text);
  };

  const saveEdit = () => {
    if (editingIndex === null) return;
    const trimmedLabel = editLabel.trim();
    const trimmedText = editText.trim();
    if (trimmedLabel || trimmedText) {
      const updated = [...issues];
      updated[editingIndex] = {
        ...updated[editingIndex],
        label: trimmedLabel,
        text: trimmedText,
      };
      onChange(updated);
      trackCustomEvent(TrackingEventsV2.MatterContextIssueEdit);
    }
    setEditingIndex(null);
  };

  const handleDelete = (index: number) => {
    onChange(issues.filter((_, i) => i !== index));
    trackCustomEvent(TrackingEventsV2.MatterContextIssueDelete);
  };

  return (
    <>
      <SettingsListHeader
        label={issues.length === 0 ? 'No issues added' : `${issues.length} issue${issues.length === 1 ? '' : 's'}`}
        onAdd={() => setShowAddModal(true)}
        addLabel="Add issue"
      />

      {issues.map((issue, i) => (
        <SettingsListItem
          key={issue.id ?? `new-${i}`}
          onClick={() => openEditModal(i)}
          leading={<span className="text-sm text-gray-400 tabular-nums">{i + 1}.</span>}
          title={issue.label}
          description={issue.text && issue.text !== issue.label ? issue.text : undefined}
          actions={
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="iconSm" aria-label="Issue options" className="text-gray-400">
                  <Icon className="lucide--ellipsis size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openEditModal(i)}>
                  <Icon className="lucide--pencil" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleDelete(i)}
                  className="text-red-600 focus:bg-red-50 focus:text-red-700 [&>svg]:text-red-600"
                >
                  <Icon className="lucide--trash-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          }
        />
      ))}

      {/* Add Issue Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Icon className="lucide--plus" />
              Add Issue
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="add-issue-label">Label</Label>
              <Input
                id="add-issue-label"
                onChange={(e) => setAddLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
                placeholder="e.g. Breach of Contract"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="add-issue-text">
                Description <span className="text-gray-400">(optional)</span>
              </Label>
              <Textarea
                id="add-issue-text"
                value={addText}
                onChange={(e) => setAddText(e.target.value)}
                rows={3}
                placeholder="Additional detail about this issue..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={!addLabel.trim()}>
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Issue Modal */}
      <Dialog open={editingIndex !== null} onOpenChange={(open) => !open && setEditingIndex(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Icon className="lucide--pencil" />
              Edit Issue
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="edit-issue-label">Label</Label>
              <Input id="edit-issue-label" value={editLabel} onChange={(e) => setEditLabel(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-issue-text">Description</Label>
              <Textarea id="edit-issue-text" value={editText} onChange={(e) => setEditText(e.target.value)} rows={4} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditingIndex(null)}>
              Cancel
            </Button>
            <Button onClick={saveEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
